import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, Save } from 'lucide-react';
import {
  FlowEditor,
  emptyFlowValues,
  validateFlow,
  type FlowEditorValues,
} from '~/components/FlowEditor/FlowEditor';
import { useCreateFlow } from '~/lib/flows-queries';
import { useTenantContext } from '~/lib/tenantContext';
import { formatPbError } from '~/lib/pb-error';
import styles from './_authed.automations.templates_.editor.module.css';

export const Route = createFileRoute('/_authed/automations/flows_/new')({
  component: NewFlowPage,
});

function NewFlowPage() {
  const navigate = useNavigate();
  const { tenants, selectedTenantId } = useTenantContext();
  const create = useCreateFlow();

  // Defaultear al cliente activo (no al primero de la lista).
  const defaultTenant = selectedTenantId ?? tenants[0]?.id ?? '';
  const [values, setValues] = useState<FlowEditorValues>(() =>
    emptyFlowValues(defaultTenant),
  );
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setError(null);
    const validationError = validateFlow(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      const created = await create.mutateAsync({
        tenant: values.tenant,
        name: values.name,
        description: values.description,
        trigger_type: values.trigger_type,
        trigger_config: values.trigger_config,
        is_active: values.is_active,
      });
      void navigate({
        to: '/automations/flows/$flowId',
        params: { flowId: created.id },
      });
    } catch (err) {
      setError(formatPbError(err, 'Error al guardar el flujo'));
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/automations/flows" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden /> Flujos
        </Link>
        <div className={styles.titleRow}>
          <h2 className="h4" style={{ margin: 0 }}>Nuevo flujo</h2>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={create.isPending}
            >
              <Save size={14} aria-hidden />
              {create.isPending ? 'Creando…' : 'Crear flujo'}
            </button>
          </div>
        </div>
        {error && <p className={styles.errorBanner}>{error}</p>}
      </header>

      <FlowEditor
        mode="new"
        values={values}
        onChange={setValues}
        tenants={tenants}
        saving={create.isPending}
      />
    </div>
  );
}
